import * as Sentry from "@client/telemetry/sentry";

// Accepts any Storage-shaped target (localStorage, sessionStorage, test
// stubs) without depending on the DOM lib type.
type WritableStorage = { setItem(key: string, value: string): void };

// One Sentry report per key per session: a failing persist loop must not
// flood telemetry, but a second failing key is a distinct signal. Failures
// are never fully suppressed — every one still logs to the console.
const reportedKeys = new Set<string>();

/**
 * Storage write that cannot throw into the caller. On a quota-exhausted
 * device (the S-PROCESS-FRONTEND-8C state) every setItem throws — even a
 * one-byte preference — so any unguarded write turns storage pressure into a
 * broken click handler, a failed sign-in, or (inside an effect) an unmounted
 * app. Returns whether the write landed; callers whose correctness depends
 * on durability must check it, best-effort callers can ignore it.
 */
export function trySetItem(
  storage: WritableStorage,
  key: string,
  value: string,
): boolean {
  try {
    storage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn("[storage] write failed", {
      key,
      bytes: value.length,
      error: String(error),
    });
    if (!reportedKeys.has(key)) {
      reportedKeys.add(key);
      Sentry.captureMessage(
        `storage write failed: ${key} (${value.length}B): ${String(error)}`,
        "warning",
      );
    }
    return false;
  }
}

export function resetStorageWriteReportsForTests(): void {
  reportedKeys.clear();
}
